import { colors, Select } from '@insurancemenu/imenu-components-lib';
import { InsertChart, Mail, Reload } from '@insurancemenu/imenu-components-lib/dist/icons';
import ActionModal from 'components/common/modals/ActionModal';
import { ModalType } from 'global/utils/ModalsHelper';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useRootStore } from 'store/rootStore';
import PeriodsModalTable from './PeriodsModalTable';
import { createStyles } from './styles';

const WarningModal = observer((): JSX.Element => {
  const classes = createStyles();
  const {
    triggerRenewalsStore: {
      isWarningModalOpen,
      triggerModalType,
      isSelectAllButtonChecked,
      selectedPeriods,
      selectedGroups,
      currentPeriod,
      currentGroup,
      paginationDelegate: { filters },
      closeWarningModal,
      closeStatusesModal,
      sendRenewalDigest,
      triggerAllRenewal,
      moveAllToRenewal,
      deleteSelectedEnrollments,
      triggerSelectedRenewals,
      moveSelectedPeriodsToRenewals,
      getAvailableEffectiveDateOptions,
      updateEffectiveDate,
    },
  } = useRootStore();
  const convertedSelectedPeriods = selectedPeriods.map((selectedPeriod) => toJS(selectedPeriod)).slice();
  const convertedSelectedGroups = selectedGroups.map((selectedGroup) => toJS(selectedGroup)).slice();
  const convertedPeriod = toJS(currentPeriod);
  const convertedGroup = toJS(currentGroup);

  const selectedPeriodIds = convertedSelectedPeriods.map((selectedPeriod) => selectedPeriod.id);

  const [periodEffectiveDate, setPeriodEffectiveDate] = useState(convertedPeriod.effectiveDate);

  useEffect(() => {
    if (!periodEffectiveDate && triggerModalType === 'CHANGE_EFFECTIVE_DATE') {
      setPeriodEffectiveDate(convertedPeriod.effectiveDate);
    }
  }, [convertedPeriod]);

  const emailsWarningMessage = (
    <span>
      <b className={classes.modalNote}>Note:</b> Emails send in a queue, so to receive actual list, pls reload page in
      couple of minutes.
    </span>
  );

  switch (triggerModalType) {
    case 'GET_STATUSES':
      return (
        <ActionModal
          show={isWarningModalOpen}
          onClose={closeStatusesModal}
          title="New statuses will be updated in a minute."
          modalType={ModalType.NOTIFICATION}
        />
      );
    case 'SEND_STATISTIC':
      return (
        <ActionModal
          show={isWarningModalOpen}
          onClose={closeWarningModal}
          onAction={async (): Promise<void> => {
            await sendRenewalDigest(filters.sponsorId, filters.effectiveDate);
            closeWarningModal();
          }}
          actionButtonText="Send Statistic"
          title="Please Confirm an Action"
          icon={<InsertChart width="5.6rem" color={colors.green[600]} />}
        />
      );
    case 'TRIGGER_ALL':
      return (
        <ActionModal
          show={isWarningModalOpen}
          onClose={closeWarningModal}
          onAction={async (): Promise<void> => {
            closeWarningModal();
            await triggerAllRenewal(filters.sponsorId, filters.effectiveDate);
          }}
          title="Please Confirm an Action"
          subtitle={
            <p className={classes.modalNotes}>
              <span>This action will move all periods to renewal and send emails.</span>
              {emailsWarningMessage}
            </p>
          }
          actionButtonText="Trigger All"
          icon={<Mail width="5.6rem" color={colors.green[600]} />}
        />
      );
    case 'TRIGGER_SELECTED':
      return (
        <ActionModal
          show={isWarningModalOpen}
          onClose={closeWarningModal}
          onAction={async (): Promise<void> => {
            closeWarningModal();
            await triggerSelectedRenewals(selectedPeriodIds);
          }}
          title="Please Confirm an Action"
          subtitle={
            <p className={classes.modalNotes}>
              <span>This action will move periods to renewal and send emails.</span>
              {emailsWarningMessage}
            </p>
          }
          actionButtonText="Trigger"
          icon={<Mail width="5.6rem" color={colors.green[600]} />}
          modalContent={
            isSelectAllButtonChecked ? undefined : (
              <PeriodsModalTable periods={convertedSelectedPeriods} groups={convertedSelectedGroups} />
            )
          }
        />
      );
    case 'MOVE_ALL_TO_RENEWAL':
      return (
        <ActionModal
          show={isWarningModalOpen}
          onClose={closeWarningModal}
          onAction={async (): Promise<void> => {
            closeWarningModal();
            await moveAllToRenewal(filters.sponsorId, filters.effectiveDate);
          }}
          title="Please Confirm an Action"
          subtitle={
            <p className={classes.modalNotes}>
              <span>This action will move the all periods to renewal without sending emails.</span>
              {emailsWarningMessage}
            </p>
          }
          actionButtonText="Move All to Renewal"
          icon={<Reload width="5.6rem" color={colors.green[600]} className={classes.reloadIcon} />}
        />
      );
    case 'MOVE_SELECTED_PERIODS_TO_RENEWAL':
      return (
        <ActionModal
          show={isWarningModalOpen}
          onClose={closeWarningModal}
          onAction={async (): Promise<void> => {
            closeWarningModal();
            await moveSelectedPeriodsToRenewals(selectedPeriodIds);
          }}
          title="Please Confirm an Action"
          actionButtonText="Move to Renewal"
          subtitle={
            <p className={classes.modalNotes}>
              <span>This action will move selected periods to renewal without sending emails.</span>
              {emailsWarningMessage}
            </p>
          }
          icon={<Reload width="5.6rem" color={colors.green[600]} className={classes.reloadIcon} />}
          modalContent={
            isSelectAllButtonChecked ? undefined : (
              <PeriodsModalTable periods={convertedSelectedPeriods} groups={convertedSelectedGroups} />
            )
          }
        />
      );
    case 'REMOVE_PERIOD':
      return (
        <ActionModal
          show={isWarningModalOpen}
          onClose={closeWarningModal}
          onAction={async (): Promise<void> => {
            closeWarningModal();
            await deleteSelectedEnrollments([convertedPeriod]);
            // The request to retrieve the updated groups list was not completed because the total elements count does not update all at once,
            // and the periods that were changed are updated within each row.
          }}
          title="Please Confirm an Action"
          subtitle="This action will remove the current period. You will no longer be able to make any changes for this period."
          actionButtonText="Remove period"
          modalType={ModalType.DELETE}
          modalContent={<PeriodsModalTable periods={[convertedPeriod]} groups={[convertedGroup]} />}
        />
      );
    case 'REMOVE_PERIODS':
      return (
        <ActionModal
          show={isWarningModalOpen}
          onClose={closeWarningModal}
          onAction={async (): Promise<void> => {
            closeWarningModal();
            await deleteSelectedEnrollments(convertedSelectedPeriods);
            // The request to retrieve the updated groups list was not completed because the total elements count does not update all at once,
            // and the periods that were changed are updated within each row.
          }}
          title="Please Confirm an Action"
          subtitle={`This action will remove ${convertedSelectedPeriods.length} selected periods. You will no longer be able to make any changes for these periods.`}
          actionButtonText="Remove periods"
          modalType={ModalType.DELETE}
          modalContent={
            isSelectAllButtonChecked ? undefined : (
              <PeriodsModalTable periods={convertedSelectedPeriods} groups={convertedSelectedGroups} />
            )
          }
        />
      );
    case 'CHANGE_EFFECTIVE_DATE':
      return (
        <ActionModal
          show={isWarningModalOpen}
          onClose={(): void => {
            closeWarningModal();
            setPeriodEffectiveDate('');
          }}
          onAction={async (): Promise<void> => {
            closeWarningModal();
            await updateEffectiveDate(convertedPeriod.id, periodEffectiveDate);
          }}
          title="Change renewal effective date"
          subtitle="Choose your desired effective date for this period from the dropdown below."
          actionButtonText="Save"
          modalType={ModalType.NOTIFICATION}
          modalContent={
            <PeriodsModalTable
              periods={[convertedPeriod]}
              groups={[convertedGroup]}
              changeableEffectiveDate={
                <Select
                  name="renewalEffectiveDate"
                  options={getAvailableEffectiveDateOptions(convertedPeriod)}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
                    setPeriodEffectiveDate(e.target.value);
                  }}
                  value={periodEffectiveDate}
                  width="auto"
                />
              }
            />
          }
        />
      );

    default:
      return <></>;
  }
});

export default WarningModal;
